/**
 * Business data load orchestration — read → hydrate → phase apply → unlock.
 * Robustness helpers live in businessLoadSupport / businessLoadRecovery.
 */

import { logAchatsReload } from '../../utils/achatReloadDebug';
import { purgeLegacyStandaloneDomainKeys } from '../../utils/localStorageHealth';
import {
  businessDataStorageKey,
  normalizeBusinessStorageId
} from '../../utils/unifiedDataHelpers';
import { kvGetRaw } from '../../infrastructure/storage/kvAccess';
import { unwrapEnvelopeRawStringToInnerJsonString } from '../../utils/localStorageCompression.js';
import { markBootGate } from '../../infrastructure/telemetry/bootTelemetry';
import {
  isDeferPayloadConsolidationOnBootEnabled,
  isDashboardShellGateEnabled
} from '../../infrastructure/startup/bootFeatureFlags';
import { scheduleAggregateIndexBackfill } from '../../infrastructure/startup/bootStorageDeferrals';
import { scheduleStartupIdle } from '../../infrastructure/startup/scheduleStartupIdle';
import {
  EAGER_ENTITY_READ_BUDGET_MS,
  ENTITY_FIELD_READ_CONCURRENCY,
  ENTITY_RESYNC_DEADLINE_MS,
  PHASE2_WRITER_WAIT_MS,
  buildBatchDataFromParsed,
  runSoft,
  warnLoad,
  withDeadline
} from './businessLoadSupport';
import { waitForEntityStoreWritersIdle } from '../../infrastructure/storage/entityStoreActivityState';
import {
  ensureEntityHydrateBounded,
  finalizeBusinessLoadUnlock,
  unlockRecoveredOrEmpty
} from './businessLoadRecovery';
import {
  isLazyBusinessBootEnabled,
  importAllEntityReaders,
  readEntityFieldsParallel,
  mergeCanonicalFields,
  resyncEntityStoresIfCoreEmpty,
  applyRestaurantProductPrep,
  applyBatchFieldsToSetters,
  initializeInstantCacheFromBatch,
  postProcessSupermarketInvoicesBatch,
  runAggregateIndexBackfill,
  executeLazyPhasedLoad,
  BOOT_SHELL_FIELDS,
  SHELL_ONLY_FIELDS,
  LAZY_BOOT_PRIORITY_FIELDS,
  LAZY_BOOT_DEFERRED_FIELDS
} from './businessLoadPhases';

function describePurchasesRaw(purchasesRaw) {
  if (Array.isArray(purchasesRaw)) {
    return { kind: 'array', count: purchasesRaw.length };
  }
  if (purchasesRaw && typeof purchasesRaw === 'object') {
    return { kind: 'object', count: Object.keys(purchasesRaw).length };
  }
  return { kind: 'absent', count: 0 };
}

function parseSavedPayloadRaw(savedData) {
  if (savedData == null || savedData === '') return null;
  try {
    return typeof savedData === 'string' ? JSON.parse(savedData) : savedData;
  } catch (err) {
    warnLoad('fallback payload JSON parse', err);
    return null;
  }
}

async function readParsedBusinessPayload(
  businessId,
  { skipMergedBlobRead, noKvBlobMode, entityAuthority }
) {
  if (skipMergedBlobRead) {
    markBootGate('business_blob_read_skipped', { entityAuthority, noKvBlobMode });
    // Still hydrate thin-shell fields (openTickets / drafts / tables) — not in entity_*.
    let shellBase = {
      entityStorePrimary: true,
      entityStoreNoKvBlob: noKvBlobMode
    };
    try {
      const { readBestBusinessMergedPayload } = await import('../../utils/sharedCore');
      const { mergeNoKvBlobShellFields } = await import(
        '../../infrastructure/storage/businessPayloadAssembly'
      );
      const best = readBestBusinessMergedPayload(businessId);
      if (best?.parsed) {
        shellBase = mergeNoKvBlobShellFields(shellBase, best.parsed);
        markBootGate('business_shell_fields_merged', {
          openTickets: Array.isArray(shellBase.openTickets) ? shellBase.openTickets.length : 0,
          drafts: Array.isArray(shellBase.drafts) ? shellBase.drafts.length : 0
        });
      }
    } catch (err) {
      warnLoad('thin shell merge skipped', err);
    }
    return shellBase;
  }

  try {
    const { readBestBusinessMergedPayload, normalizeBusinessPayloadShape } = await import(
      '../../utils/sharedCore'
    );
    const best = readBestBusinessMergedPayload(businessId);
    const parsedData = best.parsed ? normalizeBusinessPayloadShape(best.parsed) : null;
    markBootGate('business_blob_read_done', {
      hasParsed: !!parsedData,
      score: best?.score ?? null
    });

    const runConsolidation = () =>
      import('../../utils/sharedCore').then(({ consolidateBusinessMergedPayloadToCanonical }) => {
        consolidateBusinessMergedPayloadToCanonical(businessId);
      });
    if (isDeferPayloadConsolidationOnBootEnabled()) {
      markBootGate('payload_consolidation_deferred');
      scheduleStartupIdle(() => void runConsolidation(), 5000);
    } else {
      void runConsolidation();
    }
    return parsedData;
  } catch (err) {
    warnLoad('merged payload read — trying direct KV fallback', err);
    const dataKey = businessDataStorageKey(businessId);
    const legacyKey = `business_${businessId}_data`;
    const rawCanonical = dataKey ? kvGetRaw(dataKey) : null;
    const rawLegacy = dataKey !== legacyKey ? kvGetRaw(legacyKey) : null;
    const savedData =
      rawCanonical != null
        ? unwrapEnvelopeRawStringToInnerJsonString(dataKey, rawCanonical)
        : rawLegacy != null
          ? unwrapEnvelopeRawStringToInnerJsonString(legacyKey, rawLegacy)
          : null;
    return parseSavedPayloadRaw(savedData);
  }
}

function kickoffSequenceRecovery() {
  void runSoft('sequence recovery', async () => {
    const { recoverSequencesOnStartup } = await import(
      '../../infrastructure/storage/sequencesMigration'
    );
    recoverSequencesOnStartup();
    const { default: isoStandardIdGenerator } = await import(
      '../../utils/ISOStandardIdGenerator'
    );
    isoStandardIdGenerator.reloadSequences();
  });
}

async function executeEagerFullLoad({
  businessId,
  batchData,
  noKvBlobMode,
  shellBlob,
  setters,
  setDataLoaded,
  setDataShellReady,
  setReady,
  startTransition
}) {
  await withDeadline(
    (async () => {
      const readers = await importAllEntityReaders();
      try {
        const writerWait = await waitForEntityStoreWritersIdle({
          maxWaitMs: PHASE2_WRITER_WAIT_MS,
          label: 'eagerFullLoad'
        });
        if (writerWait.timedOut) {
          warnLoad('Eager load starting while entity writers still active', writerWait);
        }
      } catch (waitErr) {
        warnLoad('Eager writer wait skipped', waitErr);
      }
      const allEntityFields = [...LAZY_BOOT_PRIORITY_FIELDS, ...LAZY_BOOT_DEFERRED_FIELDS];
      const canonicalByField = await readEntityFieldsParallel(
        businessId,
        allEntityFields,
        readers,
        {
          concurrency: ENTITY_FIELD_READ_CONCURRENCY,
          batchLabel: 'eagerFullLoad'
        }
      );
      mergeCanonicalFields(batchData, canonicalByField, { businessId });

      await runSoft('eager entity resync', () =>
        withDeadline(
          resyncEntityStoresIfCoreEmpty(batchData, businessId, noKvBlobMode, readers),
          ENTITY_RESYNC_DEADLINE_MS,
          'eager_entity_resync'
        )
      );
      await runSoft('eager restaurant prep', () =>
        applyRestaurantProductPrep(batchData, businessId)
      );
      await runSoft('eager supermarket invoice post-process', () =>
        postProcessSupermarketInvoicesBatch(batchData, businessId)
      );

      logAchatsReload('load.applyBatch', {
        businessId,
        purchasesNormalizedCount: batchData.purchases?.length ?? 0,
        samplePurchaseIds: (batchData.purchases || [])
          .slice(0, 5)
          .map((p) => p?.id ?? p?.purchaseId ?? '?'),
        shellBlob
      });

      const allApplyFields = [...allEntityFields, ...SHELL_ONLY_FIELDS];
      if (isDashboardShellGateEnabled() && setDataShellReady) {
        applyBatchFieldsToSetters(
          batchData,
          setters,
          [...BOOT_SHELL_FIELDS, ...SHELL_ONLY_FIELDS],
          {
            startTransition,
            setDataShellReady,
            markDataShellReady: true,
            urgent: true
          }
        );
        markBootGate('data_shell_ready', {
          productsCount: batchData.products?.length ?? 0
        });
        if (typeof setReady === 'function') setReady(true);

        const shellFieldSet = new Set([...BOOT_SHELL_FIELDS, ...SHELL_ONLY_FIELDS]);
        const remainingFields = allApplyFields.filter((field) => !shellFieldSet.has(field));
        applyBatchFieldsToSetters(batchData, setters, remainingFields, {
          startTransition,
          setDataLoaded,
          markDataLoaded: true,
          urgent: true
        });
      } else {
        applyBatchFieldsToSetters(batchData, setters, allApplyFields, {
          startTransition,
          setDataLoaded,
          markDataLoaded: true,
          urgent: true
        });
        if (typeof setDataShellReady === 'function') setDataShellReady(true);
        if (typeof setReady === 'function') setReady(true);
      }

      initializeInstantCacheFromBatch(batchData, businessId);
      runSoft('eager legacy key purge', () => {
        purgeLegacyStandaloneDomainKeys(businessId);
      });
      scheduleAggregateIndexBackfill(() => runAggregateIndexBackfill(batchData, businessId));
    })(),
    EAGER_ENTITY_READ_BUDGET_MS,
    'eager_full_load'
  );
}

export async function executeBusinessLoad(
  rawBusinessId,
  { setters, setDataLoaded, setDataShellReady, setReady, startTransition }
) {
  const businessId = normalizeBusinessStorageId(rawBusinessId);
  if (!businessId) return;

  const canonicalKey = businessDataStorageKey(businessId);
  const legacyKey = `business_${businessId}_data`;
  const unlockCtx = {
    businessId,
    setters,
    setDataLoaded,
    setDataShellReady,
    startTransition
  };

  let finalized = false;
  const finalizeOnce = async () => {
    if (finalized) return;
    finalized = true;
    await finalizeBusinessLoadUnlock({ businessId, setReady, setDataShellReady });
  };

  try {
    markBootGate('business_load_start', { businessId });
    logAchatsReload('load.start', {
      businessId,
      canonicalKey,
      legacyKey,
      keysDiffer: canonicalKey !== legacyKey
    });

    // Deferred business_*_data must be in cache before sync blob/entity hydrate reads
    try {
      const { ensureBusinessPayloadKeysLoaded } = await import(
        '../../infrastructure/storage/businessPayloadFreshRead'
      );
      await ensureBusinessPayloadKeysLoaded(businessId);
      markBootGate('business_payload_keys_ready', { businessId });
    } catch (keysErr) {
      warnLoad('business payload key preload skipped', keysErr);
    }

    const { isEntityStoreNoKvBlobEnabled } = await import(
      '../../infrastructure/storage/businessPayloadMeta'
    );
    const { isEntityStoreAuthorityEnabled } = await import(
      '../../infrastructure/storage/entityAggregateStore'
    );
    const { isSkipMergedBlobWhenEntityAuthorityEnabled } = await import(
      '../../infrastructure/startup/bootFeatureFlags'
    );

    const noKvBlobMode = isEntityStoreNoKvBlobEnabled();
    const entityAuthority = isEntityStoreAuthorityEnabled();
    const skipMergedBlobRead =
      isSkipMergedBlobWhenEntityAuthorityEnabled() && (entityAuthority || noKvBlobMode);

    kickoffSequenceRecovery();

    let parsedData = await readParsedBusinessPayload(businessId, {
      skipMergedBlobRead,
      noKvBlobMode,
      entityAuthority
    });

    if (!parsedData && (noKvBlobMode || entityAuthority)) {
      parsedData = { entityStorePrimary: true, entityStoreNoKvBlob: noKvBlobMode };
    }

    const purchasesMeta = describePurchasesRaw(parsedData?.purchases);
    logAchatsReload('load.afterRead', {
      businessId,
      hasParsedData: !!parsedData,
      localStorageCanonicalHit: !!(canonicalKey && kvGetRaw(canonicalKey)),
      localStorageLegacyHit: canonicalKey !== legacyKey && !!kvGetRaw(legacyKey),
      purchasesRawKind: purchasesMeta.kind,
      purchasesRawCount: purchasesMeta.count
    });

    const { scoreBusinessMergedPayload: scorePayload, isEntityShellBlob } = await import(
      '../../utils/sharedCore'
    );
    const payloadScore = scorePayload(parsedData);
    const shellBlob = isEntityShellBlob(parsedData);
    const hasLoadablePayload =
      parsedData && (payloadScore > 0 || shellBlob || noKvBlobMode || entityAuthority);

    if (hasLoadablePayload) {
      const batchData = buildBatchDataFromParsed(parsedData, businessId);

      if (noKvBlobMode || entityAuthority) {
        const { wasAutomaticRecoveryHydrated } = await import(
          '../../infrastructure/storage/recoveryPipeline'
        );
        if (!wasAutomaticRecoveryHydrated(businessId)) {
          await ensureEntityHydrateBounded(businessId);
        } else {
          markBootGate('entity_hydrate_skipped_recovery_pipeline', { businessId });
        }
      }

      if (isLazyBusinessBootEnabled()) {
        await executeLazyPhasedLoad({
          businessId,
          batchData,
          noKvBlobMode,
          shellBlob,
          entityAuthority,
          setters,
          setDataLoaded,
          setDataShellReady,
          setReady,
          startTransition
        });
      } else {
        try {
          await executeEagerFullLoad({
            businessId,
            batchData,
            noKvBlobMode,
            shellBlob,
            setters,
            setDataLoaded,
            setDataShellReady,
            setReady,
            startTransition
          });
        } catch (eagerErr) {
          // Budget exceeded or hard failure — recover shell instead of empty catalog.
          warnLoad('eager full load failed — recovering shell', eagerErr);
          markBootGate('business_load_eager_failed', {
            error: eagerErr?.message || String(eagerErr)
          });
          await unlockRecoveredOrEmpty({
            ...unlockCtx,
            gate: {
              recovered: 'business_load_degraded',
              empty: 'business_load_empty_fallback'
            },
            emergencyLabel: 'eager_fail_emergency_shell',
            reason: eagerErr?.message || String(eagerErr)
          });
        }
      }
    } else {
      logAchatsReload('load.emptyBucket', {
        businessId,
        canonicalKey,
        legacyKey,
        payloadScore,
        hint: 'Kein sinnvoller Business-Blob (score 0) — prüfe entity_* / business_*_data'
      });

      const outcome = await unlockRecoveredOrEmpty({
        ...unlockCtx,
        gate: {
          recovered: 'business_load_recovered_empty_bucket',
          empty: 'business_load_new_business_defaults'
        },
        emergencyLabel: 'empty_bucket_emergency_shell',
        reason: 'empty_bucket'
      });

      if (!outcome.recovered && process.env.NODE_ENV === 'development') {
        console.info('[businessLoad] New business — empty local payload, using defaults', {
          businessId,
          canonicalKey,
          payloadScore
        });
      }
    }
  } catch (error) {
    logAchatsReload('load.exception', {
      businessId,
      canonicalKey,
      legacyKey,
      error: error?.message || String(error)
    });
    markBootGate('business_load_failed', {
      error: error?.message || String(error)
    });

    await unlockRecoveredOrEmpty({
      ...unlockCtx,
      gate: {
        recovered: 'business_load_degraded',
        empty: 'business_load_empty_fallback'
      },
      emergencyLabel: 'exception_emergency_shell',
      reason: error?.message || String(error)
    });
  } finally {
    await finalizeOnce();
  }
}
